import { useMemo } from 'react'
import { getParentDeviceMap, useDevices } from '../../../data/devices.ts'
import { type ExpandedMeasurement, useLastReportedMeasurement, useLiveMeasurements } from '../../../data/liveMeasurements.ts'
import { convertIdToMeasurementType, convertMeasurementTypeToId, type MeasurementTypeUnit } from '../../../data/measurementTypes.ts'
import { type UserPreferences, usePreferences } from '../../../data/userProfile.ts'
import { dayjs } from '../../../dayjs.ts'
import { isMeasurementTypeBlacklisted } from '../measurementBlacklist.ts'
import { useMeasurementUtils } from '../useMeasurementUtils.ts'

export const useMeasurementsTableData = () => {
	const { data: liveMeasurements } = useLiveMeasurements()
	const { data: devices } = useDevices()
	const lastReportedRecord = useLastReportedMeasurement()
	const selectedAQI = usePreferences()?.data?.unitsAirQuality
	const { getPrimaryMeasurement, getLowestQuality } = useMeasurementUtils()

	const parentDeviceMap = useMemo(() => {
		return getParentDeviceMap(devices)
	}, [devices])

	const tableInfo = useMemo(() => {
		const columnIds = new Set<string>()
		const rowInfo = {} as RowInfo

		Object.entries(liveMeasurements).forEach(([sensorId, measurements]) => {
			const parentDeviceId = parentDeviceMap.get(sensorId)
			if (!parentDeviceId) {
				return
			}

			Object.entries(measurements).forEach(([measurementTypeId, measurement]) => {
				if (isNotSelectedAQI(measurement, selectedAQI) || isMeasurementTypeBlacklisted(measurement.measurementType)) {
					return
				}
				const combinedMeasurementTypeId = combineMeasurementTypeId(measurementTypeId)
				columnIds.add(combinedMeasurementTypeId)
				if (!rowInfo[parentDeviceId]) {
					rowInfo[parentDeviceId] = {}
				}
				if (!rowInfo[parentDeviceId][combinedMeasurementTypeId]) {
					rowInfo[parentDeviceId][combinedMeasurementTypeId] = []
				}
				rowInfo[parentDeviceId][combinedMeasurementTypeId].push(measurement)
			})
		})

		return {
			columnIds: Array.from(columnIds),
			rows: devices.map((device) => {
				const lastReported = lastReportedRecord[device.id]
				let goodStatus = lastReported === undefined ? false : dayjs().diff(dayjs(lastReported), 'hour', true) < 2
				const measurements = Object.entries(rowInfo[device.id] ?? {}).reduce(
					(result, [measurementTypeId, measurements]) => {
						const measurement = getPrimaryMeasurement(measurements, 'table')
						if (!measurement || measurement.length === 0) {
							return result
						}
						result[measurementTypeId] = measurement
						const worstQuality = getLowestQuality(measurement)
						if (worstQuality && worstQuality.qualityValue < 0) {
							goodStatus = false
						}
						return result
					},
					{} as Record<string, ExpandedMeasurement[]>,
				)

				return { ...device, lastReported, measurements, goodStatus }
			}),
		}
	}, [liveMeasurements, parentDeviceMap, devices, lastReportedRecord, selectedAQI, getPrimaryMeasurement, getLowestQuality])

	return tableInfo
}

// ParentDeviceId -> MeasurementTypeId -> ExpandedMeasurement[]
type RowInfo = Record<string, Record<string, ExpandedMeasurement[]>>

const isNotSelectedAQI = (measurement: ExpandedMeasurement, selectedAQI?: UserPreferences['unitsAirQuality']) => {
	const aqiUnitName: MeasurementTypeUnit = selectedAQI === 'USAQI' ? 'USA_EPA_AIR_NOW' : 'EEA_EAQI'
	return measurement.measurementType.parameter === 'AIR_QUALITY_INDEX' && measurement.measurementType.unit !== aqiUnitName
}

const combineMeasurementTypeId = (measurementTypeId: string) => {
	const measurementType = convertIdToMeasurementType(measurementTypeId)
	if (!measurementType) {
		return measurementTypeId
	}

	const strippedMeasurementType = {
		...measurementType,
	}
	strippedMeasurementType.sourceId = undefined

	// These need to keep their height, as thats the only difference
	if (measurementType.parameter !== 'SUB_SURFACE_TEMPERATURE') {
		strippedMeasurementType.height = 0
	}

	// We have both  PT1S and PT0S periods, but they are pretty much the same and use same tooltips
	if (dayjs.duration(measurementType.period).asSeconds() <= 60) {
		strippedMeasurementType.period = '_'
	}

	// Present weather is shown as a single column regardless of period, so collapse 1/15/60 min together.
	if (measurementType.parameter === 'PRESENT_WEATHER') {
		strippedMeasurementType.period = '_'
	}

	// MEAN and VALUE are very similar, so we can group them together, wind speed needs to keep it.
	if (measurementType.parameter !== 'WIND_SPEED' && (measurementType.statistic === 'MEAN' || measurementType.statistic === 'VALUE')) {
		strippedMeasurementType.statistic = '_'
	}

	return convertMeasurementTypeToId(strippedMeasurementType)
}
